import { Link } from 'react-router-dom'
import { Logo } from '@/components/Logo'

export function PrivacyPolicyPage() {
  const lastUpdated = 'March 24, 2025'

  return (
    <div className="min-h-screen bg-[#f6f6f8] dark:bg-[#111621] text-[#0e121b] dark:text-white">
      {/* Floating NavBar */}
      <div className="fixed top-6 left-0 right-0 z-50 flex justify-center px-4">
        <header className="flex items-center justify-between w-full max-w-[960px] bg-white/80 dark:bg-[#1c2230]/80 backdrop-blur-md border border-gray-200 dark:border-gray-800 rounded-full px-6 py-2.5 shadow-sm">
          <Link to="/">
            <Logo size="md" variant="auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <a className="text-sm font-medium hover:text-[#1754cf] transition-colors" href="/#features">Features</a>
            <Link className="text-sm font-medium hover:text-[#1754cf] transition-colors" to="/pricing">Pricing</Link>
            <Link className="text-sm font-medium hover:text-[#1754cf] transition-colors" to="/login">Login</Link>
          </nav>
          <Link
            to="/signup"
            className="bg-[#1754cf] text-white text-sm font-bold px-5 py-2 rounded-full hover:opacity-90 transition-opacity"
          >
            Get Started
          </Link>
        </header>
      </div>

      <main className="pt-36 pb-20">
        <div className="max-w-[760px] mx-auto px-6">
          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1754cf]/10 border border-[#1754cf]/20 w-fit mb-6">
              <span className="material-symbols-outlined text-[14px] text-[#1754cf]">shield</span>
              <span className="text-xs font-bold text-[#1754cf] tracking-wide uppercase">Privacy Policy</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-[-0.04em] mb-4">
              Your privacy,<br /><span className="text-[#1754cf]">our priority.</span>
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              Last updated: <span className="font-semibold text-[#0e121b] dark:text-white">{lastUpdated}</span>
            </p>
          </div>

          {/* Content */}
          <div className="space-y-10">
            <Section title="1. Introduction">
              <p>
                URLzippy ("we", "our", or "us") operates the website <strong>urlzippy.com</strong> and related services (the "Service"). This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our Service.
              </p>
              <p>
                By using URLzippy, you agree to the collection and use of information in accordance with this policy. If you do not agree, please do not use our Service.
              </p>
            </Section>

            <Section title="2. Information We Collect">
              <SubHeading>Account Information</SubHeading>
              <p>
                When you register, we collect your name, email address, and password (stored securely as a hash). If you sign up via Google, we receive your name, email, and profile picture from Google's OAuth service.
              </p>
              <SubHeading>Usage Data</SubHeading>
              <p>
                We automatically collect usage data when you interact with our Service, including IP addresses (anonymised after processing), browser type, pages visited, and timestamps. This data helps us improve our Service and generate analytics for your links.
              </p>
              <SubHeading>Link & Analytics Data</SubHeading>
              <p>
                When someone clicks a shortened URL you created, we log the click event including the approximate geographic location (country/city), referrer URL, device type, and browser. This information is presented to you as analytics in your dashboard.
              </p>
              <SubHeading>Instagram & Facebook Data</SubHeading>
              <p>
                If you connect Instagram Automation, we request access to your Instagram Business account and linked Facebook Pages via Meta's OAuth. We store a long-lived access token, your Instagram username, and the linked Facebook Page ID. We use this solely to power the DM automation features you configure. We do not sell or share this data with third parties.
              </p>
            </Section>

            <Section title="3. How We Use Your Information">
              <ul className="list-none space-y-3">
                {[
                  'To provide, operate, and improve the URLzippy Service',
                  'To generate click analytics and reports for your shortened links',
                  'To authenticate your identity and secure your account',
                  'To send transactional emails (password resets, account notifications)',
                  'To power Instagram Automation features you have enabled',
                  'To detect fraud, abuse, and ensure platform security',
                  'To comply with legal obligations',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="material-symbols-outlined text-[#1754cf] text-[18px] mt-0.5 flex-shrink-0">check_circle</span>
                    <span className="text-gray-700 dark:text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section title="4. Third-Party Services">
              <p>We use the following third-party services that have their own privacy practices:</p>
              <div className="mt-4 space-y-4">
                {[
                  { name: 'Supabase', desc: 'Database and authentication provider. Your account data is stored securely on Supabase infrastructure.' },
                  { name: 'Meta (Facebook & Instagram)', desc: 'Used for Instagram Automation OAuth. When you connect Instagram, Meta processes authentication data per their Data Policy.' },
                  { name: 'Google OAuth', desc: 'Used for "Continue with Google" sign-in. Google processes authentication data per their Privacy Policy.' },
                  { name: 'Cloudflare Turnstile', desc: 'Used for bot protection on public URL shortening. No personal data is collected.' },
                  { name: 'Sentry', desc: 'Used for error monitoring to improve reliability. Error reports may contain anonymised user session context.' },
                ].map(({ name, desc }) => (
                  <div key={name} className="p-4 rounded-xl bg-white dark:bg-[#1c2230] border border-gray-200 dark:border-gray-800">
                    <p className="font-bold text-sm text-[#1754cf] mb-1">{name}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{desc}</p>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="5. Cookies">
              <p>
                We use cookies and similar tracking technologies to maintain your session and remember your preferences (e.g. dark mode). We do not use cookies for third-party advertising.
              </p>
              <p>
                You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service (e.g. staying logged in).
              </p>
            </Section>

            <Section title="6. Data Retention">
              <p>
                We retain your account data for as long as your account is active. Link analytics data is retained for up to 2 years. If you delete your account, we will delete your personal data within 30 days, except where we are required to retain it for legal or security reasons.
              </p>
              <p>
                Instagram and Facebook access tokens are stored securely and automatically rotated before expiry. Revoking access via your Facebook Business Integrations page will invalidate our access token.
              </p>
            </Section>

            <Section title="7. Data Security">
              <p>
                We implement industry-standard security measures including encryption in transit (TLS), encrypted storage of sensitive tokens, and access control. However, no method of transmission over the Internet or method of electronic storage is 100% secure. We strive to use commercially acceptable means to protect your personal data.
              </p>
            </Section>

            <Section title="8. Your Rights">
              <p>
                Depending on your location, you may have the following rights regarding your personal data:
              </p>
              <ul className="list-none space-y-3 mt-4">
                {[
                  'Access – Request a copy of the personal data we hold about you',
                  'Correction – Request we correct inaccurate or incomplete data',
                  'Deletion – Request we delete your personal data ("right to be forgotten")',
                  'Portability – Request a machine-readable export of your data',
                  'Objection – Object to processing of your personal data in certain circumstances',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="material-symbols-outlined text-[#1754cf] text-[18px] mt-0.5 flex-shrink-0">arrow_forward</span>
                    <span className="text-gray-700 dark:text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us at the email below. We will respond within 30 days.
              </p>
            </Section>

            <Section title="9. Children's Privacy">
              <p>
                Our Service is not directed to individuals under the age of 13. We do not knowingly collect personally identifiable information from children under 13. If you become aware that a child has provided us with personal data, please contact us immediately.
              </p>
            </Section>

            <Section title="10. Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically for any changes.
              </p>
            </Section>

            <Section title="11. Contact Us">
              <p>
                If you have any questions about this Privacy Policy or your personal data, please contact us:
              </p>
              <div className="mt-4 p-6 rounded-2xl bg-white dark:bg-[#1c2230] border border-gray-200 dark:border-gray-800">
                <p className="font-bold text-lg mb-1">URLzippy</p>
                <p className="text-gray-600 dark:text-gray-400 text-sm">Email: <a href="mailto:privacy@urlzippy.com" className="text-[#1754cf] hover:underline">privacy@urlzippy.com</a></p>
                <p className="text-gray-600 dark:text-gray-400 text-sm">Website: <a href="https://urlzippy.com" className="text-[#1754cf] hover:underline">urlzippy.com</a></p>
              </div>
            </Section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="max-w-[960px] mx-auto px-6 py-12 border-t border-gray-200 dark:border-gray-800">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <Logo size="sm" variant="auto" />
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-sm text-[#1754cf] font-medium">Privacy Policy</Link>
            <Link to="/pricing" className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">Pricing</Link>
          </div>
          <p className="text-sm text-gray-400">© {new Date().getFullYear()} URLzippy. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-bold mb-4 text-[#0e121b] dark:text-white border-b border-gray-200 dark:border-gray-800 pb-3">
        {title}
      </h2>
      <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
        {children}
      </div>
    </section>
  )
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <p className="font-bold text-[#0e121b] dark:text-white mt-4 mb-1">{children}</p>
  )
}
